// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useQuery } from '@tanstack/react-query';
import { getRoles } from '../services/roleService';
import { useSharedSession } from '../auth/useSharedSession';
import { toUserRole } from '../../helios/services/authService';
import type { UserRole } from '../../helios/types';
import type { RoleDTO } from '../types/iam';

/** One tenant role, enriched with its canonical Admin Center role (when it
 *  has one) so callers don't each re-derive it via `toUserRole`. */
export interface RoleWithCanonical extends RoleDTO {
  /** Owner/Admin/Member/Viewer-mapped key, or null for a role outside that
   *  taxonomy (e.g. a tenant-defined custom role like "Site Auditor"). */
  canonicalRole: UserRole | null;
}

export interface RolesView {
  /** Every role `GET /api/v1/roles` returns, including its real permissions
   *  (`RoleDTO.permissions`) — unlike `useRoleCatalog`, nothing here is
   *  dropped or deduplicated down to the four canonical roles. */
  roles: RoleWithCanonical[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Raw tenant role catalogue, permissions included, for Access > Roles (the
 * permission matrix). `useRoleCatalog` exists for the Invite/Edit User role
 * pickers and intentionally collapses this down to one entry per canonical
 * role with no permissions — not reused here because the Roles screen needs
 * the opposite: every role IAM returns, with what it actually grants.
 */
export function useRoles(): RolesView {
  const { organizationId, isLoading: sessionLoading } = useSharedSession();

  const query = useQuery({
    queryKey: ['admin-center', 'roles-with-permissions', organizationId],
    queryFn: () => getRoles(),
    enabled: !sessionLoading,
  });

  const roles = (query.data ?? []).map(role => ({
    ...role,
    canonicalRole: toUserRole(role.roleName),
  }));

  return {
    roles,
    isLoading: sessionLoading || query.isPending,
    error: query.isError ? 'Unable to load roles.' : null,
  };
}
